import { memo, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { T } from '../../../translation/src';
import { PropertyPanel } from '../../Properties/components/PropertyPanel';
import { TypeFilterButton, useTypeFilter } from './typeFilter';

interface ChildPropertiesSectionProps {
  projectId: string;
  /** The group's child annotations (e.g. a floor's rooms or a surface's segments) */
  annotations: { identifier: string; type: string }[];
}

/**
 * Combined properties of a group's children. The header + indent signal that these
 * belong inside the selected object, distinct from its own properties above.
 * Collapsible, with a filter (by type, or subtype for openings/structures) when
 * more than one distinct kind is present.
 */
const ChildPropertiesSection_: React.FC2<ChildPropertiesSectionProps> = ({ projectId, annotations }) => {
  const { availableKeys, selectedKeys, toggleKey, visibleIdentifiers, visibleTypes, visibleAnnotations, isGroupView } = useTypeFilter(annotations);

  const [open, setOpen] = useState(true);

  if (annotations.length === 0) return null;

  return (
    <div className="pb-16">
      <div className="mb-3 flex items-center pr-2 text-sm font-bold dark:text-white">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 cursor-pointer hover:text-black dark:hover:text-neon-green-300 text-[#6F7173]">
          {open ? <ChevronDownIcon className="w-3.5 h-3.5" /> : <ChevronRightIcon className="w-3.5 h-3.5" />}
          <T _str="elements" swc />
        </button>
        {availableKeys.length > 1 && <TypeFilterButton className="ml-auto" availableKeys={availableKeys} selectedKeys={selectedKeys} toggleKey={toggleKey} />}
      </div>
      {open && visibleIdentifiers.length > 0 && (
        <div className="pl-7">
          <PropertyPanel projectId={projectId} annotationTypes={visibleTypes} objectIdentifiers={visibleIdentifiers} selectedAnnotations={visibleAnnotations} isGroupView={isGroupView} />
        </div>
      )}
    </div>
  );
};

export const ChildPropertiesSection = memo(ChildPropertiesSection_);
